import Dexie, { type Table } from 'dexie'

/**
 * Base locale d'Ariariko.
 *
 * Toute l'interface lit et écrit ICI, jamais directement sur le VPS.
 * La synchronisation tourne en arrière-plan (voir sync.ts).
 * RÈGLE : ne jamais `await` la synchro dans un gestionnaire de clic.
 *
 * Périmètre actuel : la tranche verticale transactions + catégories, plus le
 * portefeuille qui les porte. Les 16 autres features viendront sur le même
 * patron, une fois le moteur de sync éprouvé.
 */

/** Colonnes portées par toute ligne synchronisée. */
export interface Syncable {
  id: string                  // UUIDv7 généré par le client
  /** Version attribuée par le serveur. 0 = jamais encore synchronisé. */
  _version: number
  /**
   * Horloge locale de la dernière modification — arbitre "dernière date gagne".
   * Nommée explicitement `client_updated_at` : côté serveur, `updated_at` est
   * l'horloge du serveur, et confondre les deux fausse l'arbitrage.
   */
  client_updated_at: string
  /** Tombstone. Une ligne supprimée reste en base, sinon rien ne se propage. */
  deleted_at: string | null
}

export type Direction = 'income' | 'expense'

/** Le rythme du budget, donné par la périodicité du revenu de l'utilisateur. */
export type Cycle = 'monthly' | 'weekly'

/**
 * Comment une enveloppe se dépense. Exclusifs entre eux.
 *
 *   none   au fil de l'eau, rien n'est prévu d'avance
 *   once   une seule échéance à confirmer — le loyer, une facture
 *   daily  réparti sur les jours du cycle — la nourriture, le transport
 */
export type PlanMode = 'none' | 'once' | 'daily'

export interface Wallet extends Syncable {
  name: string
  /** Propriété du PORTEFEUILLE, pas de chaque enveloppe : tout s'aligne dessus. */
  cycle: Cycle
  /**
   * Le revenu attendu par cycle — l'estimation posée à l'installation, et
   * reproposée à chaque nouveau cycle.
   *
   * C'est un PLAN, jamais une écriture. Aucun revenu n'est créé à partir de
   * cette valeur sans que l'utilisateur ait confirmé ce qu'il a RÉELLEMENT
   * reçu : beaucoup n'ont pas de revenu fixe, et un salaire qui n'arrive pas
   * ferait afficher un solde qui n'existe pas. 0 = pas d'estimation.
   */
  expected_income: number | null
  /**
   * L'utilisateur a-t-il rangé ses enveloppes lui-même ?
   *
   * Tant que non, l'écran Budget descend en bas les catégories sans montant.
   * Dès qu'il glisse une ligne, son ordre devient la vérité : rien n'est plus
   * déroutant qu'un rangement fait à la main qui se défait tout seul.
   */
  manual_category_order: boolean
}

export interface Category extends Syncable {
  wallet_id: string
  name: string
  direction: Direction
  icon: string | null
  position: number
}

export interface Transaction extends Syncable {
  wallet_id: string
  category_id: string | null
  entered_by_account_id: string | null
  /** Ariary, entier, toujours positif — c'est `direction` qui porte le signe. */
  amount: number
  direction: Direction
  operation_date: string      // AAAA-MM-JJ
  /**
   * De quoi il s'agit, en quelques mots — « marché », « écolage de Rina ».
   * C'est ce qui NOMME la ligne dans l'historique.
   */
  title: string | null
  /**
   * Le détail, sur plusieurs lignes si besoin. On ne le lit pas dans une liste :
   * il répond à « pourquoi ce montant ? » quand on rouvre l'opération.
   */
  note: string | null
}

/**
 * Une enveloppe : le montant affecté à une catégorie pour un cycle.
 *
 * Ces lignes ne sont jamais modifiées rétroactivement. Changer le montant pour
 * octobre insère une NOUVELLE ligne datée du 1er octobre ; celle de septembre
 * reste intacte. Modifier la ligne du cycle EN COURS est en revanche permis —
 * le cycle n'est pas encore de l'histoire.
 */
export interface Budget extends Syncable {
  wallet_id: string
  category_id: string
  /** Ariary, entier. Le montant affecté pour un cycle. */
  amount: number
  /** Toujours un début de cycle (AAAA-MM-JJ). */
  effective_from: string
  plan_mode: PlanMode
  /**
   * L'épargne : son reste ne retombe jamais dans l'argent à placer. Sinon
   * l'utilisateur réaffecte les mêmes 50 000 chaque mois en croyant épargner.
   */
  carries_over: boolean
}

/**
 * Une dépense PRÉVUE à une date, dans une enveloppe.
 *
 * Volontairement distincte de `Transaction`, qui ne veut dire qu'une chose : de
 * l'argent a vraiment bougé. Tant que `confirmed_at` est nul, rien n'a été
 * dépensé et rien ne doit entrer dans un solde.
 */
export interface BudgetAllocation extends Syncable {
  wallet_id: string
  category_id: string
  planned_date: string        // AAAA-MM-JJ
  planned_amount: number
  /** Libellé libre — « riz », « viande ». Aucune taxonomie, aucun arbre. */
  label: string | null
  confirmed_at: string | null
  /** La dépense réelle créée à la confirmation. */
  transaction_id: string | null
}

/** Une opération en attente d'envoi au serveur. */
export interface OutboxOperation {
  seq?: number                        // ordre d'insertion = ordre d'envoi
  entity: string
  id: string                          // UUIDv7 généré par le client
  op: 'upsert' | 'delete'
  data: unknown
  base_version: number                // version sur laquelle le client s'appuie
  status: 'pending' | 'in_flight' | 'failed'
  batch_id?: string                   // stable entre deux tentatives
  attempts: number                    // garde-fou contre l'opération empoisonnée
}

/**
 * Un conflit que l'utilisateur doit trancher (garde-fou de vision.md).
 *
 * Les DEUX versions sont conservées telles quelles : tant que personne n'a
 * choisi, aucune des deux n'est perdue. C'est la règle qui sépare un conflit
 * d'un écrasement silencieux.
 */
export interface Conflict {
  id: string
  entity: string
  local_version: unknown
  server_version: unknown
  /**
   * Le numéro de version du serveur.
   *
   * Indispensable pour ADOPTER sa version : sans lui, la ligne locale
   * repartirait avec une base périmée et le prochain envoi rouvrirait le même
   * conflit, indéfiniment. Absent sur les conflits détectés avant le
   * 2026-08-24 — comparer avec `?? 0`.
   */
  server_row_version?: number
  detected_at: string
}

export interface MetaEntry {
  key: string
  value: unknown
}

/**
 * Un objectif d'épargne — feature 9, décision 2026-08-24 (soir).
 *
 * ⚠️ Distinct de l'enveloppe cumulative « Épargne ». Deux mécanismes de mise de
 * côté coexistent, et ils ne partagent AUCUN argent : ce qui est versé sur un
 * objectif n'est pas dans l'enveloppe, et réciproquement. L'écran doit le dire,
 * sinon l'utilisateur croira réserver deux fois les mêmes ariary.
 *
 * Cagnotte virtuelle : ce qui est versé réduit l'argent À PLACER, jamais
 * l'argent EN POCHE. Aucun ariary ne bouge — c'est une intention. C'est
 * pourquoi un versement n'est SURTOUT PAS une transaction (invariant 3).
 */
export interface Goal extends Syncable {
  wallet_id: string
  name: string
  /** Ariary, entier. 0 = pas de cible chiffrée. */
  target_amount: number
  /** Facultative : « mettre de côté pour une moto » vaut sans date. */
  target_date: string | null
  icon: string | null
  position: number
  /**
   * Retenu plutôt que recalculé : sinon la félicitation se rejouerait à chaque
   * ouverture de l'écran, ce qui la vide de son sens.
   */
  achieved_at: string | null
  archived_at: string | null
}

/** Un mouvement vers un objectif, ou depuis lui. */
export interface GoalDeposit extends Syncable {
  wallet_id: string
  goal_id: string
  /** Toujours POSITIF — c'est `direction` qui porte le signe, comme partout. */
  amount: number
  direction: 'deposit' | 'withdrawal'
  moved_on: string
  note: string | null
}

/**
 * D'où vient une notification. Sert à choisir son icône, et à regrouper.
 *
 * `shared` est le seul genre qui ne puisse pas se calculer sur l'appareil —
 * il décrit une décision prise par quelqu'un d'autre, et n'arrivera donc que
 * par la synchronisation ou par un push serveur.
 */
export type NotificationKind =
  | 'reminder'   // une échéance prévue — dépense récurrente, cotisation
  | 'budget'     // une enveloppe dépassée
  | 'goal'       // un objectif d'épargne atteint
  | 'cycle'      // un cycle à clôturer
  | 'shared'     // une décision prise par un autre membre du portefeuille
  | 'system'     // l'app elle-même

/**
 * Une notification, adressée à UN compte.
 *
 * ⚠️ Nommée `AppNotification` et non `Notification` : ce dernier est un type
 * GLOBAL du navigateur, et le masquer rendrait le vrai inaccessible le jour où
 * le service worker en affichera.
 *
 * ⚠️ **La seule entité synchronisée qui suive le COMPTE et non le
 * portefeuille** (décision 2026-09-05). Le principe « tout ce qui est partagé
 * suit le portefeuille » vaut pour la RÈGLE qui déclenche un rappel ; la
 * notification qu'elle produit s'adresse à une personne. Un portefeuille à
 * trois membres produit donc TROIS lignes — sinon la première lue le serait
 * pour tout le monde.
 *
 * `wallet_id` reste, mais comme contexte : d'où ça vient. Il est facultatif —
 * « ton mot de passe a été changé » ne relève d'aucun portefeuille.
 *
 * `occurred_at` et non `created_at` : ce dernier appartient à Rails côté
 * serveur, et le champ métier dit quand l'ÉVÉNEMENT a eu lieu — ce qui peut
 * précéder de plusieurs jours son arrivée sur cet appareil.
 */
export interface AppNotification extends Syncable {
  /** Le destinataire. C'est lui qui cloisonne, ici comme sur le serveur. */
  account_id: string
  wallet_id: string | null
  kind: NotificationKind
  title: string
  body: string | null
  /** L'écran à ouvrir quand on la touche. `null` = elle ne mène nulle part. */
  route: string | null
  occurred_at: string
  /** Quand elle a été lue. `null` = non lue. Synchronisé : lu ici, lu partout. */
  read_at: string | null
}

export class AriarikoDatabase extends Dexie {
  wallets!: Table<Wallet, string>
  categories!: Table<Category, string>
  transactions!: Table<Transaction, string>
  budgets!: Table<Budget, string>
  budget_allocations!: Table<BudgetAllocation, string>
  goals!: Table<Goal, string>
  goal_deposits!: Table<GoalDeposit, string>
  notifications!: Table<AppNotification, string>
  outbox!: Table<OutboxOperation, number>
  conflicts!: Table<Conflict, string>
  meta!: Table<MetaEntry, string>

  constructor(name = 'ariariko') {
    super(name)
    // `deleted_at` est indexé parce que TOUTE lecture métier doit l'exclure —
    // afficher une transaction supprimée, c'est afficher un solde faux.
    this.version(1).stores({
      outbox: '++seq, status, batch_id, id, entity',
      conflicts: 'id, entity',
      meta: 'key',
      wallets: 'id, deleted_at',
      categories: 'id, wallet_id, deleted_at, [wallet_id+deleted_at]',
      transactions:
        'id, wallet_id, category_id, operation_date, deleted_at, [wallet_id+deleted_at]',
    })

    // Le budget à enveloppes. Les index composés servent les deux seules
    // questions posées à chaud : « les enveloppes de ce cycle » et « ce qui est
    // prévu aujourd'hui ».
    this.version(2).stores({
      budgets:
        'id, wallet_id, category_id, effective_from, deleted_at, ' +
        '[wallet_id+deleted_at], [wallet_id+category_id]',
      budget_allocations:
        'id, wallet_id, category_id, planned_date, deleted_at, ' +
        '[wallet_id+deleted_at], [wallet_id+planned_date]',
    })

    // Planifier ses journées interroge le prévu d'UNE enveloppe sur une période
    // — un index de plus, pas une table. Une version distincte plutôt qu'une
    // retouche de la v2 : une base déjà créée en v2 doit pouvoir migrer.
    this.version(3).stores({
      budget_allocations:
        'id, wallet_id, category_id, planned_date, deleted_at, ' +
        '[wallet_id+deleted_at], [wallet_id+planned_date], [wallet_id+category_id]',
    })

    // Les objectifs d'épargne. Une version de plus, jamais une retouche des
    // précédentes : une base déjà créée en v3 doit pouvoir migrer.
    this.version(4).stores({
      goals: 'id, wallet_id, deleted_at, [wallet_id+deleted_at]',
      goal_deposits:
        'id, wallet_id, goal_id, moved_on, deleted_at, ' +
        '[wallet_id+deleted_at], [goal_id+deleted_at]',
    })

    // Les notifications. Indexées par COMPTE et non par portefeuille : c'est
    // le destinataire qui les cloisonne. `occurred_at` l'est aussi, parce que
    // la liste se lit toujours de la plus récente à la plus ancienne — jamais
    // autrement. `read_at` ne l'est PAS : IndexedDB n'indexe pas les valeurs
    // nulles, et « les non lues » est justement une question sur des nulls.
    // Elle se tranche en mémoire, comme `deleted_at` partout ailleurs ici.
    this.version(5).stores({
      notifications:
        'id, account_id, occurred_at, deleted_at, ' +
        '[account_id+deleted_at], [account_id+occurred_at]',
    })
  }
}

export const db = new AriarikoDatabase()

/** Les entités que le moteur de sync sait transporter, dans l'ordre de dépendance. */
export const SYNC_ENTITIES = [
  'wallets', 'categories', 'transactions', 'budgets', 'budget_allocations',
  'goals', 'goal_deposits',
  // En dernier, et sans dépendance : une notification ne s'appuie sur rien
  // d'autre — son `wallet_id` est facultatif, et le serveur ne le vérifie pas.
  'notifications',
] as const
export type SyncEntity = (typeof SYNC_ENTITIES)[number]
